import i18n from "i18next";
import { initReactI18next, useTranslation as useReactTranslation } from "react-i18next";
import { addLocale, locale as setPrimeLocale } from "primereact/api";
import moment from "moment";
import "moment/locale/vi";
import "moment/locale/zh-cn";
import "moment/locale/ja";
import "moment/locale/ko";

import { AppStorage } from "../../service/UserStorage";
import { resources, Locale } from "./resources";

export const LOCALE_STORAGE_KEY = "locale";
export const SUPPORTED_LOCALES: Locale[] = ["vi", "en", "zh", "ja", "ko"];

const primeLocales = {
  vi: {
    startsWith: "Bắt đầu bằng",
    contains: "Chứa",
    notContains: "Không chứa",
    endsWith: "Kết thúc bằng",
    equals: "Bằng",
    notEquals: "Không bằng",
    noFilter: "Không lọc",
    filter: "Lọc",
    clear: "Xóa",
    close: "Đóng",
    apply: "Áp dụng",
    matchAll: "Khớp tất cả",
    matchAny: "Khớp bất kỳ",
    addRule: "Thêm điều kiện",
    removeRule: "Xóa điều kiện",
    accept: "Có",
    reject: "Không",
    choose: "Chọn",
    upload: "Tải lên",
    cancel: "Hủy",
    today: "Hôm nay",
    emptyFilterMessage: "Không có lựa chọn",
    emptyMessage: "Không tìm thấy kết quả",
    rowsPerPageLabel: "Số dòng mỗi trang",
    dateFormat: "dd/mm/yy",
    firstDayOfWeek: 1,
    aria: {
      trueLabel: "Đúng",
      falseLabel: "Sai",
      nullLabel: "Chưa chọn",
      pageLabel: "Trang",
      firstPageLabel: "Trang đầu",
      lastPageLabel: "Trang cuối",
      nextPageLabel: "Trang sau",
      previousPageLabel: "Trang trước",
      selectLabel: "Chọn",
      unselectLabel: "Bỏ chọn",
      expandLabel: "Mở rộng",
      collapseLabel: "Thu gọn",
    },
  },
  en: {
    startsWith: "Starts with",
    contains: "Contains",
    notContains: "Not contains",
    endsWith: "Ends with",
    equals: "Equals",
    notEquals: "Not equals",
    noFilter: "No filter",
    filter: "Filter",
    clear: "Clear",
    close: "Close",
    apply: "Apply",
    matchAll: "Match all",
    matchAny: "Match any",
    addRule: "Add rule",
    removeRule: "Remove rule",
    accept: "Yes",
    reject: "No",
    choose: "Choose",
    upload: "Upload",
    cancel: "Cancel",
    today: "Today",
    emptyFilterMessage: "No available options",
    emptyMessage: "No results found",
    rowsPerPageLabel: "Rows per page",
    dateFormat: "mm/dd/yy",
    firstDayOfWeek: 0,
    aria: {
      trueLabel: "True",
      falseLabel: "False",
      nullLabel: "Not selected",
      pageLabel: "Page",
      firstPageLabel: "First page",
      lastPageLabel: "Last page",
      nextPageLabel: "Next page",
      previousPageLabel: "Previous page",
      selectLabel: "Select",
      unselectLabel: "Unselect",
      expandLabel: "Expand",
      collapseLabel: "Collapse",
    },
  },
  zh: {
    startsWith: "开始于",
    contains: "包含",
    notContains: "不包含",
    endsWith: "结束于",
    equals: "等于",
    notEquals: "不等于",
    noFilter: "无筛选",
    filter: "筛选",
    clear: "清除",
    apply: "应用",
    matchAll: "匹配全部",
    matchAny: "匹配任意",
    addRule: "添加条件",
    removeRule: "删除条件",
    accept: "是",
    reject: "否",
    choose: "选择",
    upload: "上传",
    cancel: "取消",
    today: "今天",
    emptyFilterMessage: "没有可选项",
    emptyMessage: "未找到结果",
    rowsPerPageLabel: "每页行数",
    dateFormat: "yy-mm-dd",
    firstDayOfWeek: 1,
    aria: {
      trueLabel: "是",
      falseLabel: "否",
      nullLabel: "未选择",
      pageLabel: "页",
      firstPageLabel: "第一页",
      lastPageLabel: "最后一页",
      nextPageLabel: "下一页",
      previousPageLabel: "上一页",
      selectLabel: "选择",
      unselectLabel: "取消选择",
      expandLabel: "展开",
      collapseLabel: "收起",
    },
  },
  ja: {
    startsWith: "次で始まる",
    contains: "含む",
    notContains: "含まない",
    endsWith: "次で終わる",
    equals: "等しい",
    notEquals: "等しくない",
    noFilter: "フィルターなし",
    filter: "フィルター",
    clear: "クリア",
    close: "閉じる",
    apply: "適用",
    matchAll: "すべて一致",
    matchAny: "いずれかに一致",
    addRule: "条件を追加",
    removeRule: "条件を削除",
    accept: "はい",
    reject: "いいえ",
    choose: "選択",
    upload: "アップロード",
    cancel: "キャンセル",
    today: "今日",
    emptyFilterMessage: "選択肢がありません",
    emptyMessage: "結果が見つかりません",
    rowsPerPageLabel: "1ページあたりの行数",
    dateFormat: "yy/mm/dd",
    firstDayOfWeek: 0,
    aria: {
      trueLabel: "はい",
      falseLabel: "いいえ",
      nullLabel: "未選択",
      pageLabel: "ページ",
      firstPageLabel: "先頭ページ",
      lastPageLabel: "最終ページ",
      nextPageLabel: "次のページ",
      previousPageLabel: "前のページ",
      selectLabel: "選択",
      unselectLabel: "選択解除",
      expandLabel: "展開",
      collapseLabel: "折りたたむ",
    },
  },
  ko: {
    startsWith: "다음으로 시작",
    contains: "포함",
    notContains: "포함하지 않음",
    endsWith: "다음으로 끝남",
    equals: "같음",
    notEquals: "같지 않음",
    noFilter: "필터 없음",
    filter: "필터",
    clear: "지우기",
    close: "닫기",
    apply: "적용",
    matchAll: "모두 일치",
    matchAny: "하나라도 일치",
    addRule: "조건 추가",
    removeRule: "조건 삭제",
    accept: "예",
    reject: "아니요",
    choose: "선택",
    upload: "업로드",
    cancel: "취소",
    today: "오늘",
    emptyFilterMessage: "선택 가능한 항목이 없습니다",
    emptyMessage: "결과가 없습니다",
    rowsPerPageLabel: "페이지당 행 수",
    dateFormat: "yy/mm/dd",
    firstDayOfWeek: 0,
    aria: {
      trueLabel: "예",
      falseLabel: "아니요",
      nullLabel: "선택하지 않음",
      pageLabel: "페이지",
      firstPageLabel: "첫 페이지",
      lastPageLabel: "마지막 페이지",
      nextPageLabel: "다음 페이지",
      previousPageLabel: "이전 페이지",
      selectLabel: "선택",
      unselectLabel: "선택 해제",
      expandLabel: "펼치기",
      collapseLabel: "접기",
    },
  },
};

export function isLocale(value: string | null | undefined): value is Locale {
  return !!value && SUPPORTED_LOCALES.includes(value as Locale);
}

export function getStoredLocale(): Locale {
  const stored = AppStorage.get(LOCALE_STORAGE_KEY);
  return isLocale(stored) ? stored : "vi";
}

function configurePrimeReact(locale: Locale): void {
  addLocale(locale, primeLocales[locale]);
  setPrimeLocale(locale);
}

function syncBrowserLocale(locale: Locale): void {
  if (typeof document !== "undefined") {
    document.documentElement.lang = locale;
  }
  const momentLocale = locale === "vi"
    ? "vi"
    : locale === "zh"
      ? "zh-cn"
      : locale === "ja"
        ? "ja"
        : locale === "ko"
          ? "ko"
          : "en";
  moment.locale(momentLocale);
  configurePrimeReact(locale);
}

void i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: getStoredLocale(),
    fallbackLng: (locale) => (["zh", "ja", "ko"].includes(locale) ? ["en", "vi"] : "vi"),
    defaultNS: "translation",
    interpolation: { escapeValue: false },
    returnNull: false,
    returnEmptyString: false,
    react: { useSuspense: false },
  });

syncBrowserLocale(getStoredLocale());

export async function changeLocale(locale: Locale): Promise<void> {
  const nextLocale = isLocale(locale) ? locale : "vi";
  await i18n.changeLanguage(nextLocale);
  AppStorage.set(LOCALE_STORAGE_KEY, nextLocale);
  syncBrowserLocale(nextLocale);

  // These services import the HTTP layer, which imports this module for error
  // handling. Keep the imports lazy to avoid a module cycle at startup.
  try {
    const [{ CultureService }, { ApplicationConfigurationService, storeApplicationLocalization }] = await Promise.all([
      import("../../service/CultureService"),
      import("../../service/ApplicationConfigurationService"),
    ]);

    try {
      await CultureService.set(nextLocale);
    } catch (error) {
      if (process.env.NODE_ENV !== "test") {
        console.warn("Unable to sync backend culture", error);
      }
    }

    try {
      // With baseURL=/api/api, this requests
      // /api/api/abp/application-configuration. The request interceptor adds
      // Accept-Language using the newly selected locale.
      const response = await ApplicationConfigurationService.get();
      storeApplicationLocalization(response);
    } catch (error) {
      if (process.env.NODE_ENV !== "test") {
        console.warn("Unable to reload application configuration", error);
      }
    }
  } catch (error) {
    // The UI locale is still persisted even when the culture-sync request is
    // unavailable. The next request will use the selected locale again, while
    // application configuration remains unchanged.
    if (process.env.NODE_ENV !== "test") {
      console.warn("Unable to load localization services", error);
    }
  }
}

export function getLocale(): Locale {
  const current = i18n.language?.split("-")[0];
  return isLocale(current) ? current : "vi";
}

export const useTranslation = useReactTranslation;
export { i18n };
